import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, type Entity, type Role, type User } from "../api";
import { useAuth } from "../auth";

interface AdminUser extends User {
  created_at: number;
}

type Tab = "users" | "entities";

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>("users");

  if (!user) return null;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <>
      <h2>Administration</h2>

      <div className="tabs">
        <button className={tab === "users" ? "active" : undefined} onClick={() => setTab("users")}>
          Utilisateurs
        </button>
        <button className={tab === "entities" ? "active" : undefined} onClick={() => setTab("entities")}>
          Entités
        </button>
      </div>

      {tab === "users" ? <UsersPanel meId={user.id} /> : <EntitiesPanel />}
    </>
  );
}

/* ========================================================================== */
/* USERS                                                                      */
/* ========================================================================== */

function UsersPanel({ meId }: { meId: string }) {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    const r = await api<{ users: AdminUser[] }>("/admin/users");
    setUsers(r.users);
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <>
      <div className="row spread" style={{ marginBottom: 10 }}>
        <div className="section-h" style={{ margin: 0 }}>Utilisateurs</div>
        <button className="btn btn-primary sm" onClick={() => setCreating(true)}>+ Utilisateur</button>
      </div>

      {creating && <CreateUser onDone={() => { setCreating(false); load(); }} />}

      {users.map((u) => (
        <UserCard key={u.id} u={u} reload={load} self={u.id === meId} />
      ))}
    </>
  );
}

function CreateUser({ onDone }: { onDone: () => void }) {
  const [identifier, setIdentifier] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("user");
  const [tempPwd, setTempPwd] = useState<string | null>(null);
  const [createdIdent, setCreatedIdent] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      const r = await api<{ id: string; identifier: string; temp_password: string }>(
        "/admin/users",
        {
          method: "POST",
          json: {
            identifier: identifier.trim().toUpperCase(),
            display_name: name.trim(),
            email: email.trim() || undefined,
            role,
          },
        }
      );
      setTempPwd(r.temp_password);
      setCreatedIdent(r.identifier);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Erreur");
    } finally { setBusy(false); }
  }

  if (tempPwd) {
    return (
      <div className="card" style={{ background: "var(--terra-soft)" }}>
        <div className="section-h" style={{ margin: 0, color: "var(--terra-deep)" }}>Compte créé</div>
        <p className="muted" style={{ marginTop: 6 }}>
          Transmettez ce mot de passe temporaire à <b>{createdIdent}</b>. Il lui sera demandé d'en choisir un nouveau dès la première connexion.
        </p>
        <div style={{
          fontFamily: "ui-monospace, Menlo, monospace", fontSize: "1.4rem",
          color: "var(--navy)", background: "#fff",
          padding: "12px 14px", border: "1px solid var(--line)", borderRadius: "var(--r-md)",
          textAlign: "center", letterSpacing: "0.04em", margin: "10px 0 12px", userSelect: "all",
        }}>{tempPwd}</div>
        <button className="btn btn-ghost full" onClick={() => navigator.clipboard?.writeText(tempPwd)}>Copier</button>
        <div style={{ height: 8 }} />
        <button className="btn btn-secondary full" onClick={onDone}>Terminé</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="card">
      <div className="section-h" style={{ margin: 0 }}>Nouvel utilisateur</div>
      <label className="section-h" style={{ marginTop: 10 }}>Identifiant (initiales)</label>
      <input value={identifier} onChange={(e) => setIdentifier(e.target.value)} autoCapitalize="characters" maxLength={8} placeholder="ex. CAF" required />
      <label className="section-h" style={{ marginTop: 10 }}>Nom complet</label>
      <input value={name} onChange={(e) => setName(e.target.value)} required />
      <label className="section-h" style={{ marginTop: 10 }}>E-mail (optionnel)</label>
      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      <label className="section-h" style={{ marginTop: 10 }}>Rôle</label>
      <select value={role} onChange={(e) => setRole(e.target.value as Role)}>
        <option value="user">Utilisateur</option>
        <option value="admin">Administrateur</option>
      </select>
      {err && <div className="error" style={{ marginTop: 10 }}>{err}</div>}
      <div style={{ height: 12 }} />
      <button className="btn btn-primary full" type="submit" disabled={busy}>
        {busy ? "Création…" : "Créer le compte"}
      </button>
    </form>
  );
}

function UserCard({ u, reload, self }: { u: AdminUser; reload: () => void; self: boolean }) {
  const [tempPwd, setTempPwd] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function reset() {
    if (!confirm(`Réinitialiser le mot de passe de ${u.identifier} ?`)) return;
    setBusy(true);
    try {
      const r = await api<{ temp_password: string }>(`/admin/users/${u.id}/reset-password`, { method: "POST" });
      setTempPwd(r.temp_password);
    } finally { setBusy(false); }
  }

  async function toggleActive() {
    if (self && u.active) { alert("Vous ne pouvez pas vous désactiver vous-même."); return; }
    setBusy(true);
    try {
      await api(`/admin/users/${u.id}`, { method: "PATCH", json: { active: !u.active } });
      reload();
    } finally { setBusy(false); }
  }

  return (
    <div className="card" style={{ opacity: u.active ? 1 : 0.55 }}>
      <div className="row spread">
        <div>
          <div style={{ fontFamily: "var(--font-serif)", fontSize: "1rem", color: "var(--navy)" }}>
            <b>{u.identifier}</b> — {u.display_name}
          </div>
          <div className="tiny">
            {u.role === "admin" ? "Administrateur" : "Utilisateur"}
            {u.email ? ` · ${u.email}` : ""}
            {u.must_change_password ? " · MDP à changer" : ""}
            {!u.active ? " · DÉSACTIVÉ" : ""}
          </div>
        </div>
      </div>
      {tempPwd ? (
        <div style={{ marginTop: 10 }}>
          <div className="muted">Nouveau mot de passe temporaire :</div>
          <div style={{
            fontFamily: "ui-monospace, Menlo, monospace", fontSize: "1.2rem",
            padding: "10px 12px", background: "var(--terra-soft)", color: "var(--terra-deep)",
            borderRadius: "var(--r-md)", margin: "6px 0", textAlign: "center", userSelect: "all",
          }}>{tempPwd}</div>
          <button className="btn btn-ghost sm" onClick={() => navigator.clipboard?.writeText(tempPwd)}>Copier</button>
        </div>
      ) : (
        <div className="row" style={{ gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          <button className="btn btn-ghost sm" onClick={reset} disabled={busy}>Réinitialiser MDP</button>
          <button className="btn btn-ghost sm" onClick={toggleActive} disabled={busy || (self && !!u.active)}>
            {u.active ? "Désactiver" : "Réactiver"}
          </button>
        </div>
      )}
    </div>
  );
}

/* ========================================================================== */
/* ENTITIES                                                                   */
/* ========================================================================== */

function EntitiesPanel() {
  const [entities, setEntities] = useState<Entity[]>([]);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    const r = await api<{ entities: Entity[] }>("/entities?include_inactive=1");
    setEntities(r.entities);
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <>
      <div className="row spread" style={{ marginBottom: 10 }}>
        <div className="section-h" style={{ margin: 0 }}>Entités</div>
        <button className="btn btn-primary sm" onClick={() => setCreating(true)}>+ Entité</button>
      </div>

      {creating && <EntityForm onDone={() => { setCreating(false); load(); }} />}

      {entities.map((e) => (
        <EntityCard key={e.id} entity={e} reload={load} />
      ))}
    </>
  );
}

const KIND_OPTIONS = [
  { v: "cabinet",   label: "Cabinet" },
  { v: "lawkorn",   label: "LAWKORN" },
  { v: "sci",       label: "SCI" },
  { v: "expertise", label: "Expertise" },
  { v: "other",     label: "Autre" },
];

function EntityForm({ entity, onDone }: { entity?: Entity; onDone: () => void }) {
  const [name, setName] = useState(entity?.name || "");
  const [shortCode, setShortCode] = useState(entity?.short_code || "");
  const [kind, setKind] = useState(entity?.kind || "expertise");
  const [color, setColor] = useState(entity?.color || "#4A7FA5");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const isEdit = !!entity;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      if (isEdit) {
        await api(`/entities/${entity!.id}`, {
          method: "PATCH",
          json: { name, short_code: shortCode, kind, color },
        });
      } else {
        await api("/entities", {
          method: "POST",
          json: { name, short_code: shortCode, kind, color },
        });
      }
      onDone();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Erreur");
    } finally { setBusy(false); }
  }

  return (
    <form onSubmit={submit} className="card">
      <div className="section-h" style={{ margin: 0 }}>{isEdit ? "Modifier l'entité" : "Nouvelle entité"}</div>
      <label className="section-h" style={{ marginTop: 10 }}>Nom</label>
      <input value={name} onChange={(e) => setName(e.target.value)} required />
      <label className="section-h" style={{ marginTop: 10 }}>Code court</label>
      <input value={shortCode} onChange={(e) => setShortCode(e.target.value)} autoCapitalize="characters" maxLength={10} required />
      <label className="section-h" style={{ marginTop: 10 }}>Type</label>
      <select value={kind} onChange={(e) => setKind(e.target.value)}>
        {KIND_OPTIONS.map((k) => <option key={k.v} value={k.v}>{k.label}</option>)}
      </select>
      <label className="section-h" style={{ marginTop: 10 }}>Couleur</label>
      <input type="color" value={color} onChange={(e) => setColor(e.target.value)} style={{ height: 44, padding: 4 }} />
      {err && <div className="error" style={{ marginTop: 10 }}>{err}</div>}
      <div style={{ height: 12 }} />
      <div className="row" style={{ gap: 8 }}>
        <button className="btn btn-primary" type="submit" disabled={busy}>
          {busy ? "…" : isEdit ? "Enregistrer" : "Créer"}
        </button>
        <button type="button" className="btn btn-ghost" onClick={onDone}>Annuler</button>
      </div>
    </form>
  );
}

function EntityCard({ entity, reload }: { entity: Entity; reload: () => void }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const purgeDate = entity.purge_scheduled_at
    ? new Date(entity.purge_scheduled_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })
    : null;

  async function deactivate() {
    const ok = confirm(
      `Désactiver "${entity.name}" ?\n\nElle disparaîtra immédiatement des listes de choix.\nTout son contenu (documents, notes, messages) sera supprimé définitivement le 1er du mois suivant.\n\nTélécharge ce que tu veux garder AVANT.`
    );
    if (!ok) return;
    setBusy(true);
    try {
      await api(`/entities/${entity.id}/deactivate`, { method: "POST" });
      reload();
    } finally { setBusy(false); }
  }

  async function reactivate() {
    setBusy(true);
    try {
      await api(`/entities/${entity.id}/reactivate`, { method: "POST" });
      reload();
    } finally { setBusy(false); }
  }

  if (editing) {
    return <EntityForm entity={entity} onDone={() => { setEditing(false); reload(); }} />;
  }

  return (
    <div className="card" style={{ opacity: entity.active ? 1 : 0.55 }}>
      <div className="row spread">
        <div className="row" style={{ gap: 10 }}>
          {entity.color && (
            <span style={{
              display: "inline-block", width: 14, height: 14, borderRadius: 2,
              background: entity.color, border: "1px solid var(--line)",
            }} />
          )}
          <div>
            <div style={{ fontFamily: "var(--font-serif)", color: "var(--navy)" }}>
              <b>{entity.short_code}</b> — {entity.name}
            </div>
            <div className="tiny">
              {entity.kind}
              {!entity.active && purgeDate && ` · suppression définitive le ${purgeDate}`}
            </div>
          </div>
        </div>
      </div>
      <div className="row" style={{ gap: 8, marginTop: 10, flexWrap: "wrap" }}>
        <button className="btn btn-ghost sm" onClick={() => setEditing(true)} disabled={busy}>Modifier</button>
        {entity.active ? (
          <button className="btn btn-ghost sm" onClick={deactivate} disabled={busy} style={{ color: "var(--terra-deep)" }}>
            Désactiver
          </button>
        ) : (
          <button className="btn btn-secondary sm" onClick={reactivate} disabled={busy}>
            Réactiver
          </button>
        )}
      </div>
    </div>
  );
}
